import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Alert,
  Box,
  CircularProgress
} from '@mui/material';
import axios from 'axios';
import { SectorSelect, type Sector } from '../common/SectorSelect';

// Full sector data from backend
interface SectorData {
  id: string;
  name: string;
  slug: string;
  synonyms?: string[];
  summary80?: string;
  toneDefaults?: string[];
  do?: string[];
  dont?: string[];
  blocklist?: string[];
  platformCues?: any;
  complianceFlags?: any;
  ageGates?: any;
  version?: number;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface Account {
  id: string;
  name: string;
  description?: string;
  sector?: string | SectorData;
  sectorLegacy?: string;
  sectorId?: string;
  organizationId: string;
  createdAt: string;
}

interface EditAccountModalProps {
  open: boolean;
  onClose: () => void;
  account: Account | null;
  onAccountUpdated: () => void;
  onDeleteAccount: (account: Account) => void;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const EditAccountModal: React.FC<EditAccountModalProps> = ({ 
  open, 
  onClose, 
  account, 
  onAccountUpdated,
  onDeleteAccount
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedSector, setSelectedSector] = useState<Sector | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetchingSector, setFetchingSector] = useState(false);
  
  // Initialize form with account data when it changes
  useEffect(() => {
    if (account) {
      setName(account.name);
      setDescription(account.description || '');
      
      // If account has a sector relationship, use it directly
      if (account.sector && typeof account.sector === 'object') {
        const sectorObj = account.sector as SectorData;
        setSelectedSector({
          id: sectorObj.id,
          name: sectorObj.name,
          slug: sectorObj.slug,
          summary80: sectorObj.summary80 || ''
        });
      }
      // If account has sectorId but no populated sector, fetch it
      else if (account.sectorId) {
        setFetchingSector(true);
        axios.get(`${API_URL}/api/sectors/${account.sectorId}`, {
          withCredentials: true
        })
        .then(response => {
          if (response.data) {
            setSelectedSector(response.data);
          }
        })
        .catch(err => {
          console.error('Failed to fetch sector:', err);
          // Fall back to legacy sector if available
          if (account.sectorLegacy) {
            setSelectedSector({
              id: 'legacy',
              slug: account.sectorLegacy.toLowerCase().replace(/\s+/g, '-'),
              name: account.sectorLegacy,
              summary80: ''
            });
          }
        })
        .finally(() => setFetchingSector(false));
      }
      // Fall back to legacy sector string
      else if (account.sectorLegacy || (typeof account.sector === 'string' && account.sector)) {
        const sectorName = account.sectorLegacy || (typeof account.sector === 'string' ? account.sector : '');
        if (sectorName) {
          setSelectedSector({
            id: 'legacy',
            slug: sectorName.toLowerCase().replace(/\s+/g, '-'),
            name: sectorName,
            summary80: ''
          });
        }
      }
    }
  }, [account]);
  
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!account) {
      return;
    }
    
    // Validate inputs
    if (!name.trim()) {
      setError('Account name is required');
      return;
    }
    
    if (!selectedSector) {
      setError('Please select a business sector');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);

      await axios.put(`${API_URL}/api/accounts/${account.id}`, {
        name: name.trim(),
        description: description.trim() || undefined,
        sectorId: selectedSector.id !== 'legacy' ? selectedSector.id : undefined,
        sector: selectedSector.id === 'legacy' ? selectedSector.name : undefined
      }, {
        withCredentials: true
      });
      
      // Reset state and close modal
      onAccountUpdated();
      onClose();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.response?.data?.error || 'Failed to update account';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  const handleClose = () => {
    setError(null);
    onClose();
  };
  
  const handleDelete = () => {
    if (account) {
      onDeleteAccount(account);
      onClose();
    }
  };
  
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Account</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Update account details
          </Typography>
          
          <TextField
            label="Account Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            margin="normal"
            required
            autoFocus
            disabled={loading}
          />
          
          <TextField
            label="Brand Description (Optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            margin="normal"
            multiline
            rows={3}
            disabled={loading}
            helperText="Provide a brief description of your brand to help us generate more accurate content"
            inputProps={{ maxLength: 500 }}
          />
          
          <SectorSelect
            value={selectedSector}
            onChange={setSelectedSector}
            required
            disabled={loading || fetchingSector}
            error={!!error && !selectedSector}
            helperText={!selectedSector && error?.includes('sector') ? error : undefined}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            variant="outlined" 
            color="error" 
            onClick={handleDelete} 
            disabled={loading || !account}
            sx={{ mr: 'auto' }}
          >
            Delete
          </Button>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Box sx={{ position: 'relative' }}>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary" 
              disabled={loading || !account}
            >
              Update Account
            </Button>
            {loading && (
              <CircularProgress
                size={24}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-12px',
                  marginLeft: '-12px',
                }}
              />
            )}
          </Box>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditAccountModal; 